<template>
  <div class="mask" @click.self="onClickClose">
    <div
      ref="dialogRoot"
      class="dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
    >
      <header class="dialog__header">
        <h2 :id="titleId" class="dialog__title">
          {{ $t("proofreadingAiCancelDialog.title") }}
        </h2>
        <button
          type="button"
          class="dialog__close"
          :aria-label="$t('proofreadingAiCancelDialog.close')"
          @click="onClickClose"
        >
          ×
        </button>
      </header>

      <div class="dialog__body">
        <p class="dialog__message">{{ $t("proofreadingAiCancelDialog.message") }}</p>
        <p class="dialog__warning">{{ $t(warningKey) }}</p>
      </div>

      <footer class="dialog__footer">
        <button type="button" class="dialog__cancel-button" @click="onClickConfirm">
          {{ $t(confirmKey) }}
        </button>
        <button type="button" class="dialog__close-button" @click="onClickClose">
          {{ $t("proofreadingAiCancelDialog.dismiss") }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

export type ProofreadingAiCancelDialogResult = boolean;

export default Vue.extend({
  props: {
    /** 30分以上経過した実行の返金キャンセル。 */
    refund: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      titleId: "proofreading-ai-cancel-dialog-title",
    };
  },
  computed: {
    warningKey(): string {
      return this.refund
        ? "proofreadingAiCancelDialog.refundNotice"
        : "proofreadingAiCancelDialog.warning";
    },
    confirmKey(): string {
      return this.refund
        ? "proofreadingAiCancelDialog.refundConfirm"
        : "proofreadingAiCancelDialog.confirm";
    },
  },
  mounted() {
    this.$nextTick(() => {
      const root = this.$refs.dialogRoot as HTMLElement | undefined;
      root?.focus();
    });
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      this.onClickClose();
    },
    onClickConfirm() {
      this.$close(true as ProofreadingAiCancelDialogResult);
    },
    onClickClose() {
      this.$close(false as ProofreadingAiCancelDialogResult);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9300;
  background-color: var(--color-overlay);
}

.dialog {
  display: flex;
  flex-direction: column;
  width: min(400px, 92vw);
  margin: 0;
  border: none;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);

  &:focus {
    outline: none;
  }
}

.dialog__header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 20px 48px 0;
}

.dialog__title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  text-align: center;
}

.dialog__close {
  position: absolute;
  top: 12px;
  right: 12px;
  border: none;
  background: transparent;
  font-size: 24px;
  line-height: 1;
  color: var(--color-text-low);
  cursor: pointer;
}

.dialog__body {
  padding: 16px 24px 8px;
}

.dialog__message,
.dialog__warning {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  text-align: left;
  color: var(--color-text-normal);
}

.dialog__warning {
  margin-top: 8px;
}

.dialog__footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 24px 24px;
}

.dialog__close-button,
.dialog__cancel-button {
  position: relative;
  width: 100%;
  padding: 12px 24px;
  border: none;
  border-radius: var(--radius-rounded);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;
}

.dialog__close-button {
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
}

.dialog__cancel-button {
  background-color: var(--color-warning);
  color: var(--color-text-on-fixed-bg);
}
</style>
